import {
  Box,
  CircularProgress,
  Fade,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  makeStyles,
  MenuItem,
  Radio,
  RadioGroup,
} from '@material-ui/core'
import { Button } from '../../../components/common/Buttons'
import Typography from '../../../components/common/Typography'
import { IconClose, IconInfo, IconSearch } from '../../../components/svg/Icons'
import { CheckBox, TextField } from '../../../components/common/Inputs'
import * as Yup from 'yup'
import { useForm } from '../../../hooks/useForm'
import makeValidationSchema from '../../../hooks/makeValidationSchema'
import { useAlertContext } from '../../../contexts/AlertContext'
import { useState } from 'react'
import { Paper, SpacingContainer } from '../../../components/common/Containers'
import { ModalDialog } from '../../../components/common/Dialogs'
import clsx from 'clsx'
import { useDispatch } from 'react-redux'
import { actions } from '../../../store/clientDetail'
import { useNavigate, useParams } from 'react-router-dom'

const dataToTrasnfer = {
  USFCR_STAFF: 'usfcr_staff',
  FILES: 'files',
  ORGANIZATION_MEMBERS: 'organization_members',
  FORMS_AND_ADJUSTED_FORMS: 'forms_and_adjusted_forms',
}

const searchFields = {
  ORG_NAME: 'orgName',
  OWNER_NAME: 'ownerName',
  EMAIL: 'email',
  SALESFORCE_ID: 'salesforceId',
  CAGE_CODE: 'cageCode',
  UEI_CODE: 'ueiCode',
}

const searchFieldOptions = [
  {
    value: searchFields.ORG_NAME,
    label: 'Company Name',
    placeholder: 'Enter company name',
  },
  {
    value: searchFields.OWNER_NAME,
    label: 'Owner Name',
    placeholder: 'Enter owner name',
  },
  {
    value: searchFields.EMAIL,
    label: 'Email Address',
    placeholder: 'Enter email address',
  },
  {
    value: searchFields.SALESFORCE_ID,
    label: 'SalesForceID',
    placeholder: 'Enter SalesForceID',
  },
  {
    value: searchFields.CAGE_CODE,
    label: 'CageCode',
    placeholder: 'Enter CageCode',
  },
  {
    value: searchFields.UEI_CODE,
    label: 'UEICode',
    placeholder: 'Enter UEICode',
  },
]

const useStyles = makeStyles(theme => ({
  infoAlert: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    color: theme.palette.gray.main,
  },
  dataToTransferTitle: {
    fontWeight: 500,
  },
  dataToTransferSubtitle: {
    color: theme.palette.gray.main,
    '& span': {
      color: theme.palette.warning.light,
      fontWeight: 700,
    },
  },
  searchMergeTo: {
    position: 'relative',
    '& button': {
      position: 'absolute',
      right: '1%',
      top: '30%',
    },
    '& .loader': {
      position: 'absolute',
      right: '3%',
      top: '44%',
    },
  },
  mergeToPaper: {
    position: 'relative',

    '& button': {
      position: 'absolute',
      right: '1%',
      top: '50%',
      transform: 'translateY(-50%)',
    },
  },
  searchRow: {
    alignItems: 'center',
  },
  searchLabel: {
    color: theme.palette.gray.main,
    marginRight: theme.spacing(1),
    whiteSpace: 'nowrap',
  },
  searchValueWrapper: {
    position: 'relative',
    '& button': {
      position: 'absolute',
      right: '1%',
      top: '36%',
      transform: 'translateY(-50%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    '& .loader': {
      position: 'absolute',
      right: '4%',
      top: '36%',
      transform: 'translateY(-50%)',
    },
  },
  resultsPaper: {
    maxHeight: 260,
    overflowY: 'auto',
    marginTop: theme.spacing(1),
  },
  resultItem: {
    position: 'relative',
    padding: theme.spacing(2),
    paddingRight: theme.spacing(6),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  resultRadio: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(2),
  },
  resultLine: {
    display: 'block',
    lineHeight: 1.45,
  },
  resultLabel: {
    fontWeight: 500,
  },
  noResultsPaper: {
    marginTop: theme.spacing(1),
  },
}))

const useScheme = makeValidationSchema(() => {
  return Yup.object().shape({
    searchBy: Yup.string().required('Please select a search field'),
    searchValue: Yup.string().required('Please enter a search value'),
    mergeTo: Yup.string().required('Please select organization'),
    dataToTransfer: Yup.array().required('Please select data to transfer'),
  })
})

const initialValues = {
  searchBy: '',
  searchValue: '',
  mergeTo: '',
  dataToTransfer: [],
}

const formOptions = {
  validateOnMount: false,
  validateOnChange: false,
  validateOnBlur: false,
  enableReinitialize: true,
}

function getSearchOption(searchBy) {
  return searchFieldOptions.find(option => option.value === searchBy)
}

function getSearchValidationError(searchBy, searchValue) {
  const value = String(searchValue || '').trim()

  if (!searchBy) {
    return 'Please select a search field'
  }

  if (!value) {
    return 'Please enter a search value'
  }

  if (searchBy === searchFields.CAGE_CODE && value.length !== 5) {
    return 'CageCode must be 5 characters'
  }

  if (searchBy === searchFields.UEI_CODE && value.length !== 12) {
    return 'UEICode must be 12 characters'
  }

  return null
}

const useMergeToOrganiztion = () => {
  const [loading, setLoading] = useState(false)
  const [mergeToOrganizations, setMergeToOrganizations] = useState([])
  const [mergeToError, setMergeToError] = useState(null)

  const dispatch = useDispatch()

  const handleSearchMergeToOrg = async ({ searchBy, searchValue, currentOrganizationId }) => {
    const validationError = getSearchValidationError(searchBy, searchValue)

    if (validationError) {
      setMergeToError(validationError)
      setMergeToOrganizations([])
      return
    }

    setMergeToOrganizations([])
    setMergeToError(null)

    try {
      setLoading(true)

      const res = await dispatch(
        actions.getOrganizationForMerge({
          [searchBy]: String(searchValue).trim(),
          currentOrganizationId,
        })
      )

      const organizations = res?.payload?.organizations ?? []

      setMergeToOrganizations(organizations)

      if (!organizations.length) {
        setMergeToError("Can't find matching organization")
      }
    } catch (err) {
      setMergeToError("Can't find matching organization")
    } finally {
      setLoading(false)
    }
  }

  const handleResetMergeToOrg = () => {
    setMergeToOrganizations([])
    setMergeToError(null)
  }

  const handleMergeSubmit = async params => {
    return Promise.resolve(dispatch(actions.mergeOrganizations(params)))
  }

  return {
    handleMergeSubmit,
    handleResetMergeToOrg,
    handleSearchMergeToOrg,
    loading,
    mergeToError,
    mergeToOrganizations,
    setLoading,
  }
}

function MergeOrganizationModal({ open, onClose, org, orgHierarchy = [] }) {
  const styles = useStyles()
  const scheme = useScheme()
  const { successAlert, errorAlert } = useAlertContext()
  const navigate = useNavigate()
  const params = useParams()

  const {
    loading,
    setLoading,
    mergeToOrganizations,
    mergeToError,
    handleSearchMergeToOrg,
    handleResetMergeToOrg,
    handleMergeSubmit,
  } = useMergeToOrganiztion()

  const onSubmit = ({ mergeTo, dataToTransfer }) => {
    const body = {
      mergeFrom: org,
      mergeTo,
      dataToTransfer,
    }

    handleMergeSubmit(body)
      .then(res => {
        if (res?.payload?.success) {
          closeModal()
          successAlert('Organization was successfully merged')
          navigate(`/organizations/${mergeTo}`)
        } else {
          errorAlert('Something went wrong')
        }
      })
      .catch(err => {
        errorAlert(err.message)
      })
      .finally(() => setLoading(false))
  }

  // eslint-disable-next-line no-unused-vars
  const { values, getFieldProps, setFieldValue, handleSubmit, resetForm } =
    useForm(scheme, initialValues, onSubmit, formOptions)

  const selectedSearchOption = getSearchOption(values.searchBy)

  const handleCheckBoxChange = e => {
    const { value, checked } = e.target

    if (checked) {
      setFieldValue('dataToTransfer', [...values.dataToTransfer, value])
    } else {
      setFieldValue(
        'dataToTransfer',
        values.dataToTransfer.filter(v => v !== value)
      )
    }
  }

  const handleSearch = () => {
    const currentOrganizationId =
      typeof org === 'string'
        ? org
        : org?.id || org?.organization?.id || org?.detail?.id || params?.id

    setFieldValue('mergeTo', '')

    handleSearchMergeToOrg({
      searchBy: values.searchBy,
      searchValue: values.searchValue,
      currentOrganizationId,
    })
  }

  const handleSearchByChange = e => {
    setFieldValue('searchBy', e.target.value)
    setFieldValue('searchValue', '')
    setFieldValue('mergeTo', '')
    handleResetMergeToOrg()
  }

  const handleSearchValueChange = e => {
    setFieldValue('searchValue', e.target.value)
    setFieldValue('mergeTo', '')
  }

  const handleSelectOrganization = e => {
    setFieldValue('mergeTo', e.target.value)
  }

  const onResetMergeToOrg = () => {
    setFieldValue('mergeTo', '')
    setFieldValue('searchValue', '')
    handleResetMergeToOrg()
  }

  const closeModal = () => {
    resetForm()
    handleResetMergeToOrg()
    onClose()
  }

  return (
    <ModalDialog
      open={open}
      onClose={closeModal}
      title="Merge organization"
      actions={
        <Grid container spacing={2} justifyContent="flex-end">
          <Grid item>
            <Button variant="outlined" color="primary" onClick={closeModal}>
              Cancel
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              pending={loading}
              disabled={loading || !values.mergeTo}
            >
              Confirm
            </Button>
          </Grid>
        </Grid>
      }
    >
      <Box className={styles.infoAlert}>
        <IconInfo />
        <Typography variant="body2">
          Current organization will be merged with another one and no longer
          exist. You can select which data you want to transfer.
        </Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid xs={12} item>
          <TextField
            name="mergeFrom"
            label="Merge from"
            disabled
            value="Current Organization"
          />
        </Grid>

        <Grid xs={12} item>
          <Typography className={styles.dataToTransferTitle} variant="body1">
            Merge to
          </Typography>
        </Grid>

        <Grid xs={12} item>
          <Grid container spacing={2} className={styles.searchRow}>
            <Grid xs={12} md={5} item>
              <Grid container alignItems="center" spacing={1}>
                <Grid item>
                  <Typography className={styles.searchLabel} variant="body2">
                    Search by:
                  </Typography>
                </Grid>
                <Grid xs item>
                  <TextField
                    select
                    name="searchBy"
                    placeholder="select field"
                    value={values.searchBy}
                    onChange={handleSearchByChange}
                    helperText=" "
                  >
                    {searchFieldOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </Grid>

            <Grid xs={12} md={7} item>
              <Grid container alignItems="center" spacing={1}>
                <Grid item>
                  <Typography className={styles.searchLabel} variant="body2">
                    Value:
                  </Typography>
                </Grid>
                <Grid xs item>
                  <Box className={styles.searchValueWrapper}>
                    <TextField
                      name="searchValue"
                      placeholder={
                        selectedSearchOption?.placeholder ?? 'Enter value'
                      }
                      value={values.searchValue}
                      onChange={handleSearchValueChange}
                      helperText=" "
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          handleSearch()
                        }
                      }}
                    />
                    {!loading ? (
                      <IconButton onClick={handleSearch}>
                        <IconSearch />
                      </IconButton>
                    ) : (
                      <span className="loader">
                        <CircularProgress color="inherit" size="1.29em" />
                      </span>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Grid xs={12} item>
          <Fade in={Boolean(mergeToOrganizations.length || mergeToError)}>
            <Box>
              {mergeToOrganizations.length > 0 && (
                <Paper
                  bgColor="paperSecondary"
                  component={SpacingContainer}
                  px={0}
                  py={0}
                  className={styles.resultsPaper}
                  radius={4}
                >
                  <RadioGroup
                    name="mergeTo"
                    value={values.mergeTo}
                    onChange={handleSelectOrganization}
                  >
                    {mergeToOrganizations.map(organization => (
                      <Box
                        key={organization.id}
                        className={styles.resultItem}
                      >
                        <Typography className={styles.resultLine}>
                          <span className={styles.resultLabel}>
                            Company Name:
                          </span>{' '}
                          {organization.orgName || 'Not Listed'}
                        </Typography>
                        <Typography className={styles.resultLine}>
                          <span className={styles.resultLabel}>
                            Owner Name:
                          </span>{' '}
                          {organization.ownerName || 'Not Listed'}
                        </Typography>
                        <Typography className={styles.resultLine}>
                          <span className={styles.resultLabel}>
                            Email Address:
                          </span>{' '}
                          {organization.email || 'Not Listed'}
                        </Typography>
                        <Typography className={styles.resultLine}>
                          <span className={styles.resultLabel}>
                            SalesForceID:
                          </span>{' '}
                          {organization.associatedSalesforceId || 'Not Listed'}
                        </Typography>
                        <Typography className={styles.resultLine}>
                          <span className={styles.resultLabel}>
                            CageCode:
                          </span>{' '}
                          {organization.cageCode || 'Not Listed'}
                        </Typography>
                        <Typography className={styles.resultLine}>
                          <span className={styles.resultLabel}>
                            UEICode:
                          </span>{' '}
                          {organization.ueiCode || 'Not Listed'}
                        </Typography>

                        <Radio
                          className={styles.resultRadio}
                          color="primary"
                          value={organization.id}
                        />
                      </Box>
                    ))}
                  </RadioGroup>
                </Paper>
              )}

              {mergeToError && !mergeToOrganizations.length && (
                <Paper
                  bgColor="error"
                  component={SpacingContainer}
                  px={2}
                  py={1}
                  className={clsx(styles.mergeToPaper, styles.noResultsPaper, 'center')}
                  radius={4}
                >
                  <Typography>{mergeToError}</Typography>
                  <IconButton onClick={onResetMergeToOrg}>
                    <IconClose />
                  </IconButton>
                </Paper>
              )}
            </Box>
          </Fade>
        </Grid>

        <Grid xs={12} item>
          <Typography className={styles.dataToTransferTitle} variant="body1">
            Data to transfer
          </Typography>
          <Typography className={styles.dataToTransferSubtitle} variant="body2">
            <span>Note!</span> All unselected and not listed data will be lost.
          </Typography>

          <FormGroup>
            <Grid container justifyContent="space-between" spacing={1}>
              <Grid xs={12} md={6} item>
                <FormControlLabel
                  control={
                    <CheckBox
                      color="secondary"
                      checked={values.dataToTransfer.includes(
                        dataToTrasnfer.USFCR_STAFF
                      )}
                      value={dataToTrasnfer.USFCR_STAFF}
                      onChange={handleCheckBoxChange}
                    />
                  }
                  label="USFCR Staff"
                />
              </Grid>

              <Grid xs={12} md={6} item>
                <FormControlLabel
                  control={
                    <CheckBox
                      color="secondary"
                      checked={values.dataToTransfer.includes(
                        dataToTrasnfer.FILES
                      )}
                      value={dataToTrasnfer.FILES}
                      onChange={handleCheckBoxChange}
                    />
                  }
                  label="Files"
                />
              </Grid>

              <Grid xs={12} md={6} item>
                <FormControlLabel
                  control={
                    <CheckBox
                      color="secondary"
                      checked={values.dataToTransfer.includes(
                        dataToTrasnfer.ORGANIZATION_MEMBERS
                      )}
                      onChange={handleCheckBoxChange}
                      value={dataToTrasnfer.ORGANIZATION_MEMBERS}
                    />
                  }
                  label="Organization Members"
                />
              </Grid>

              <Grid xs={12} md={6} item>
                <FormControlLabel
                  control={
                    <CheckBox
                      color="secondary"
                      checked={values.dataToTransfer.includes(
                        dataToTrasnfer.FORMS_AND_ADJUSTED_FORMS
                      )}
                      value={dataToTrasnfer.FORMS_AND_ADJUSTED_FORMS}
                      onChange={handleCheckBoxChange}
                    />
                  }
                  label="Worksheets and Adjusted Worksheets"
                />
              </Grid>
            </Grid>
          </FormGroup>
        </Grid>
      </Grid>
    </ModalDialog>
  )
}

export default MergeOrganizationModal